import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';

export interface DocumentDetail {
  name: string;
  path: string;
  extractedText: string;
  size?: number;
  lastModified?: Date;
  type?: string;
}

export interface DocumentInspectionData {
  userId: string;
  userName?: string;
  folderPath?: string;
  documents: DocumentDetail[];
  totalDocuments: number;
  extractionTimestamp?: Date;
}

interface DocumentInspectionModalProps {
  open: boolean;
  onClose: () => void;
  data: DocumentInspectionData | null;
  isLoading?: boolean;
  error?: string | null;
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '—';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 B';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
};

const formatDate = (date?: Date): string => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getFileTypeIcon = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return '📄';
    case 'doc':
    case 'docx':
      return '📝';
    case 'xls':
    case 'xlsx':
      return '📊';
    case 'ppt':
    case 'pptx':
      return '📑';
    default:
      return '📋';
  }
};

const DocumentInspectionModal: React.FC<DocumentInspectionModalProps> = ({
  open,
  onClose,
  data,
  isLoading = false,
  error = null
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState<Set<number>>(new Set());

  useEffect(() => {
    // Reset expanded state when modal opens
    if (open) {
      setExpandedAccordions(new Set());
    }
  }, [open]);

  const handleAccordionChange = (index: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (data?.documents) {
      setExpandedAccordions(new Set(data.documents.map((_, index) => index)));
    }
  };

  const collapseAll = () => {
    setExpandedAccordions(new Set());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: '85vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              Document Inspection
            </Typography>
            {data && (
              <Typography variant="body2" color="text.secondary">
                {data.userName || data.userId} • {data.totalDocuments} document{data.totalDocuments !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          {data && data.documents.length > 0 && (
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={expandAll}>
                Expand All
              </Button>
              <Button size="small" onClick={collapseAll}>
                Collapse All
              </Button>
            </Stack>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : data && data.documents.length > 0 ? (
          <Box>
            {data.folderPath && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FolderIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    Source Folder:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {data.folderPath}
                  </Typography>
                </Stack>
              </Paper>
            )}

            <Stack spacing={2}>
              {data.documents.map((doc, index) => (
                <Accordion
                  key={index}
                  expanded={expandedAccordions.has(index)}
                  onChange={handleAccordionChange(index)}
                  TransitionProps={{ unmountOnExit: true }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <Typography variant="body1" sx={{ mr: 1 }}>
                        {getFileTypeIcon(doc.name)}
                      </Typography>
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {doc.name}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(doc.size)}
                          </Typography>
                          {doc.lastModified && (
                            <Typography variant="caption" color="text.secondary">
                              Modified: {formatDate(doc.lastModified)}
                            </Typography>
                          )}
                          {doc.type && (
                            <Chip
                              label={doc.type}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <DescriptionIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" color="text.secondary">
                          Extracted Text:
                        </Typography>
                      </Box>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          maxHeight: 400,
                          overflow: 'auto',
                          '& pre': {
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            lineHeight: 1.6
                          }
                        }}
                      >
                        <pre>{doc.extractedText || 'No text extracted from this document.'}</pre>
                      </Paper>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>

            {data.extractionTimestamp && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Extracted on: {formatDate(data.extractionTimestamp)}
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No documents available for inspection.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentInspectionModal;